import { CalendarClock, CircleCheck, Tag, Users } from "lucide-react";
import { useGetIdentity, useTranslate } from "ra-core";
import { useMemo } from "react";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";

import { addDays, startOfDay } from "../events/calendarUtils";
import { FilterCategory } from "../filters/FilterCategory";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { AccountManagerFilter } from "../sales/AccountManagerInput";
import type { TaskStatus } from "../types";

const BUTTON_CLASS_NAME = "w-auto md:w-full justify-between h-10 md:h-8";
const TASK_STATUSES: TaskStatus[] = ["todo", "done", "abandoned"];

/** Due date ranges, from the start of today (local time). */
const useDueDateRanges = () =>
  useMemo(() => {
    const today = startOfDay(new Date());
    const daysToMonday = (8 - today.getDay()) % 7 || 7;
    const at = (days: number) => addDays(today, days).toISOString();
    return {
      overdue: { "due_date@gte": undefined, "due_date@lt": at(0) },
      today: { "due_date@gte": at(0), "due_date@lt": at(1) },
      this_week: { "due_date@gte": at(0), "due_date@lt": at(daysToMonday) },
      later: { "due_date@gte": at(daysToMonday), "due_date@lt": undefined },
    };
  }, []);

/** Sidebar of the Tasks page: status, due date, type and owner. */
export const TaskListFilter = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { taskTypes } = useConfigurationContext();
  const dueDateRanges = useDueDateRanges();

  return (
    <ResponsiveFilters>
      <FilterCategory
        label="resources.tasks.fields.status"
        icon={<CircleCheck />}
      >
        {TASK_STATUSES.map((status) => (
          <ToggleFilterButton
            key={status}
            className={BUTTON_CLASS_NAME}
            label={translate(`resources.tasks.page_statuses.${status}`)}
            value={{ status }}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        label="resources.tasks.fields.due_date"
        icon={<CalendarClock />}
      >
        {Object.entries(dueDateRanges).map(([key, value]) => (
          <ToggleFilterButton
            key={key}
            className={BUTTON_CLASS_NAME}
            label={translate(`resources.tasks.filters.${key}`)}
            value={value}
          />
        ))}
      </FilterCategory>

      <FilterCategory label="resources.tasks.fields.type" icon={<Tag />}>
        {taskTypes
          .filter((type) => type.value !== "none")
          .map((type) => (
            <ToggleFilterButton
              key={type.value}
              className={BUTTON_CLASS_NAME}
              label={type.label}
              value={{ type: type.value }}
            />
          ))}
      </FilterCategory>

      <FilterCategory
        label="resources.contacts.fields.sales_id"
        icon={<Users />}
      >
        <ToggleFilterButton
          className={BUTTON_CLASS_NAME}
          label="crm.common.me"
          value={{ sales_id: identity?.id }}
        />
        <AccountManagerFilter className={BUTTON_CLASS_NAME} />
      </FilterCategory>
    </ResponsiveFilters>
  );
};
